import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  RefreshCw,
  ShieldCheck,
  X,
  Search,
  Settings,
  MapPin,
  Users,
  Layers,
  Filter,
  ChevronDown,
} from "lucide-react";

import {
  useAdminGetDietsQuery,
  useAdminGetExamsQuery,
  useAdminGetCentersQuery,
  useAdminGetSlotsQuery,
  useAdminUpsertSlotMutation,
  useAdminDeleteSlotMutation,
} from "../../api/examsApi";

// Security & UI Patterns
import PageGate from "../../../../components/PageGate";
import AccessGate from "../../../../components/AccessGate";
import { PERMS } from "../../../../utils/perms";

function safeMsg(err: any) {
  return (
    err?.data?.message ||
    err?.data?.error ||
    err?.error ||
    err?.message ||
    "Something went wrong"
  );
}

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

/** * Tooltip Component */
function Tooltip({
  children,
  text,
}: {
  children: React.ReactNode;
  text: string;
}) {
  return (
    <div className="relative group flex items-center justify-center">
      {children}
      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block z-[60] pointer-events-none animate-in fade-in zoom-in-95 duration-100">
        <div className="bg-gray-900 text-white text-[9px] font-black uppercase tracking-[0.15em] px-2 py-1.5 rounded shadow-xl whitespace-nowrap border border-gray-700">
          {text}
        </div>
        <div className="w-2 h-2 bg-gray-900 rotate-45 mx-auto -mt-1 border-r border-b border-gray-700"></div>
      </div>
    </div>
  );
}

export default function AdminCenterSlotsPage() {
  const qs = useQuery();
  const initialDietId = qs.get("dietId") || "";
  const initialCenterId = qs.get("centerId") || "";

  const [dietId, setDietId] = useState(initialDietId);
  const [examId, setExamId] = useState("");
  const [examSearch, setExamSearch] = useState("");
  const [q, setQ] = useState("");

  const { data: diets = [] } = useAdminGetDietsQuery();
  const { data: exams = [] } = useAdminGetExamsQuery();
  const { data: centers = [] } = useAdminGetCentersQuery();

  const {
    data: slots = [],
    isLoading,
    error,
    refetch,
    isFetching,
  } = useAdminGetSlotsQuery(dietId, { skip: !dietId });

  const [upsert] = useAdminUpsertSlotMutation();
  const [remove] = useAdminDeleteSlotMutation();

  const [msg, setMsg] = useState<{
    type: "success" | "error" | "info";
    text: string;
  } | null>(null);
  const [busyCenterId, setBusyCenterId] = useState<string | null>(null);

  // Filtered Exam List for the dropdown search
  const filteredExams = useMemo(() => {
    return exams.filter((x: any) =>
      `${x.title} ${x.code}`.toLowerCase().includes(examSearch.toLowerCase())
    );
  }, [exams, examSearch]);

  const activeSlotsForExam = useMemo(() => {
    if (!examId) return [];
    return (slots || []).filter(
      (s: any) => String(s.exam_id ?? s.exam?.id) === String(examId)
    );
  }, [slots, examId]);

  const slotKeySet = useMemo(() => {
    const set = new Set<string>();
    for (const s of activeSlotsForExam) {
      const cId = s.exam_center_id ?? s.center?.id;
      if (cId) set.add(String(cId));
    }
    return set;
  }, [activeSlotsForExam]);

  const filteredCenters = useMemo(() => {
    const s = q.trim().toLowerCase();
    let list = [...centers];
    if (s) {
      list = list.filter((c: any) =>
        `${c.name} ${c.state ?? ""} ${c.address ?? ""}`
          .toLowerCase()
          .includes(s)
      );
    }
    if (initialCenterId) {
      const idx = list.findIndex(
        (x: any) => String(x.id) === String(initialCenterId)
      );
      if (idx > 0) {
        const picked = list[idx];
        return [picked, ...list.slice(0, idx), ...list.slice(idx + 1)];
      }
    }
    return list;
  }, [centers, q, initialCenterId]);

  const toggleCenter = async (centerId: string, currentlyEnabled: boolean) => {
    if (!dietId || !examId) return;

    setBusyCenterId(centerId);
    try {
      if (currentlyEnabled) {
        await remove({ dietId, examId, centerId }).unwrap();
        setMsg({ type: "info", text: "Registry Entry Revoked" });
      } else {
        await upsert({
          dietId,
          examId,
          centerId,
          body: { is_active: true },
        }).unwrap();
        setMsg({ type: "success", text: "Registry Entry Authorized" });
      }
      refetch();
    } catch (e) {
      setMsg({ type: "error", text: safeMsg(e) });
    } finally {
      setBusyCenterId(null);
    }
  };

  return (
    <PageGate perm={PERMS.exam.slotAccess}>
      <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">
        {/* 1. Industrial Header */}
        <div className="bg-white border border-gray-100 rounded-[2.5rem] p-8 shadow-sm">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
                <Layers className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none uppercase">
                  Slot Matrix
                </h1>
                <p className="text-gray-400 text-sm mt-2 font-medium italic">
                  Administrative Node Deployment Control.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/exams/admin/exams"
                className="px-5 py-2.5 rounded-xl border bg-white hover:bg-gray-50 text-[10px] font-black uppercase tracking-widest transition-all shadow-sm"
              >
                Exams
              </Link>
              <AccessGate perm={PERMS.exam.dietEdit}>
                <Tooltip text="Refresh Sync">
                  <button
                    onClick={() => refetch()}
                    className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all active:scale-95 shadow-sm"
                  >
                    <RefreshCw
                      className={`w-4 h-4 text-indigo-500 ${
                        isFetching ? "animate-spin" : ""
                      }`}
                    />
                  </button>
                </Tooltip>
              </AccessGate>
            </div>
          </div>

          {msg && (
            <div
              className={`mt-6 p-4 rounded-2xl border flex justify-between items-center animate-in fade-in slide-in-from-top-2 ${
                msg.type === "success"
                  ? "bg-emerald-50 border-emerald-100 text-emerald-800"
                  : msg.type === "error"
                  ? "bg-rose-50 border-rose-100 text-rose-800"
                  : "bg-blue-50 border-blue-100 text-blue-800"
              }`}
            >
              <span className="text-[10px] font-black uppercase tracking-widest">
                {msg.text}
              </span>
              <button onClick={() => setMsg(null)}>
                <X className="w-4 h-4 opacity-50 hover:opacity-100" />
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* 2. Configuration Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm sticky top-6">
              <div className="flex items-center gap-2 mb-6 border-b border-gray-50 pb-4">
                <Settings className="w-4 h-4 text-indigo-500" />
                <div className="font-black text-gray-900 text-[10px] uppercase tracking-widest">
                  Filters
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Active Diet
                  </label>
                  <select
                    value={dietId}
                    onChange={(e) => {
                      setDietId(e.target.value);
                      setExamId("");
                    }}
                    className="w-full border-b-2 border-gray-100 py-2 font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                  >
                    <option value="">Select Cycle...</option>
                    {diets.map((d: any) => (
                      <option key={d.id} value={d.id}>
                        {d.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest text-indigo-500">
                    Find Exam
                  </label>
                  <div className="relative">
                    <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-300" />
                    <input
                      type="text"
                      placeholder="Filter exam title/code..."
                      value={examSearch}
                      onChange={(e) => setExamSearch(e.target.value)}
                      className="w-full pl-5 border-b-2 border-gray-50 py-2 text-[11px] font-bold text-gray-600 outline-none focus:border-indigo-400 bg-transparent"
                    />
                  </div>
                  <select
                    value={examId}
                    onChange={(e) => setExamId(e.target.value)}
                    disabled={!dietId}
                    className="w-full mt-2 border-b-2 border-gray-100 py-2 font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent disabled:opacity-30"
                  >
                    <option value="">Select Exam...</option>
                    {filteredExams.map((x: any) => (
                      <option key={x.id} value={x.id}>
                        {x.title} ({x.code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* 3. Center Matrix */}
          <div className="lg:col-span-3 space-y-6">
            <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
              <div className="p-4 bg-gray-50/50 border-b border-gray-50 flex flex-col md:flex-row gap-4 items-center">
                <div className="relative flex-1 w-full">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search node name, state, or address..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    disabled={!dietId || !examId}
                    className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-2xl text-xs font-bold focus:ring-4 ring-indigo-50 transition-all outline-none disabled:bg-gray-50/50"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left bg-white border-b border-gray-50">
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                        Center Detail
                      </th>
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                        Metadata
                      </th>
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] text-center">
                        Status
                      </th>
                      <th className="px-8 py-5 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredCenters.map((c: any) => {
                      const enabled = slotKeySet.has(String(c.id));
                      const isBusy = busyCenterId === String(c.id);

                      return (
                        <tr
                          key={c.id}
                          className="group hover:bg-gray-50/80 transition-all"
                        >
                          <td className="px-8 py-6">
                            <div className="font-black text-gray-900 uppercase tracking-tight group-hover:text-indigo-600 transition-colors">
                              {c.name}
                            </div>
                            <div className="text-[10px] font-bold text-gray-400 mt-1 tracking-tighter italic truncate max-w-xs">
                              {c.address || "No Address Registry"}
                            </div>
                          </td>
                          <td className="px-8 py-6">
                            <div className="flex flex-col gap-1.5">
                              <div className="flex items-center gap-2 text-[10px] font-black text-gray-500 uppercase tracking-tighter">
                                <MapPin className="w-3 h-3 text-indigo-300" />{" "}
                                {c.state || "Unset"}
                              </div>
                              <div className="flex items-center gap-2 text-[10px] font-black text-gray-500 uppercase tracking-tighter">
                                <Users className="w-3 h-3 text-indigo-300" />{" "}
                                CAP: {c.capacity || "0"}
                              </div>
                            </div>
                          </td>
                          <td className="px-8 py-6 text-center">
                            <span
                              className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border shadow-sm inline-flex items-center gap-2 ${
                                enabled
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                  : "bg-gray-50 text-gray-400 border-gray-100"
                              }`}
                            >
                              <div
                                className={`w-1.5 h-1.5 rounded-full ${
                                  enabled ? "bg-emerald-500" : "bg-gray-300"
                                }`}
                              />
                              {enabled ? "Live" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-8 py-6 text-right">
                            <AccessGate perm={PERMS.exam.slotToggle}>
                              <Tooltip
                                text={
                                  enabled ? "Revoke Access" : "Grant Access"
                                }
                              >
                                <button
                                  disabled={isBusy || !dietId || !examId}
                                  onClick={() => toggleCenter(c.id, enabled)}
                                  className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 disabled:opacity-20 ${
                                    enabled
                                      ? "bg-rose-50 text-rose-600 border border-rose-100 hover:bg-rose-600 hover:text-white"
                                      : "bg-gray-900 text-white hover:bg-indigo-600"
                                  }`}
                                >
                                  {isBusy
                                    ? "Syncing..."
                                    : enabled
                                    ? "Disable"
                                    : "Enable"}
                                </button>
                              </Tooltip>
                            </AccessGate>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {(!dietId || !examId) && (
                  <div className="py-24 text-center">
                    <Filter className="w-12 h-12 mx-auto text-gray-100 mb-4" />
                    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-300">
                      Configuration Required
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 4. Protocols Footer */}
        <div className="bg-gray-900 text-white rounded-[2.5rem] p-10 flex flex-col md:flex-row items-center gap-10 shadow-2xl shadow-gray-200 mt-10">
          <div className="w-20 h-20 bg-indigo-500/10 rounded-3xl flex items-center justify-center shrink-0 border border-indigo-500/20">
            <ShieldCheck className="w-10 h-10 text-indigo-400" />
          </div>
          <div className="flex-1">
            <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-indigo-400 mb-3">
              System Integrity Protocol
            </h4>
            <p className="text-sm text-gray-400 leading-relaxed font-medium">
              Deployment of exam centers is a{" "}
              <span className="text-white">SYNCHRONOUS OPERATION</span>. All
              activations are logged under administrative UUID. Disabling a node
              will hide the center from the applicant registry but will{" "}
              <span className="text-white">NOT PURGE</span> existing booked
              sessions.
            </p>
          </div>
        </div>
      </div>
    </PageGate>
  );
}
